// src/components/PlaceholderPages.jsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FloatingChat from '@/components/FloatingChat';
import CharacterFlipCard from './CharacterFlipCard';
import resources from '@/data/researchResources.json';
import mapResources from '@/data/mapResources.json';
import ResearchSchema from '@/seo/ResearchSchema';

// ---- Feature flags (flip to true later to re-enable)
const ENABLE_CHAR_CHAT = false;
const ENABLE_RESEARCH_CHAT = true;

/* --------------------- Shared UI --------------------- */
const HeaderSection = ({ title, subtitle, blurb, size = 'lg' }) => {
  const sizes = {
    lg: { pad: 'py-16', h1: 'text-4xl md:text-5xl', h2: 'text-2xl md:text-3xl', p: 'text-lg', gapH1: 'mb-4', gapH2: 'mb-6' },
    md: { pad: 'py-12', h1: 'text-3xl md:text-4xl', h2: 'text-xl md:text-2xl', p: 'text-base', gapH1: 'mb-3', gapH2: 'mb-4' },
    sm: { pad: 'py-8', h1: 'text-2xl md:text-3xl', h2: 'text-lg md:text-xl', p: 'text-sm', gapH1: 'mb-2', gapH2: 'mb-3' },
  };
  const s = sizes[size] || sizes.lg;

  return (
    <div className={`bg-gradient-to-b from-stone-900 to-stone-800 ${s.pad} px-6`}>
      <div className="max-w-6xl mx-auto text-center">
        <h1 className={`${s.h1} font-bold text-white ${s.gapH1}`}>{title}</h1>
        {subtitle && <h2 className={`${s.h2} text-stone-300 ${s.gapH2}`}>{subtitle}</h2>}
        {blurb && <p className={`${s.p} text-stone-300 max-w-3xl mx-auto`}>{blurb}</p>}
      </div>
    </div>
  );
};

const SectionHeading = ({ title, subtitle, count }) => (
  <div className="max-w-6xl mx-auto px-1 mt-10 mb-4">
    <div className="flex items-end justify-between gap-4">
      <div>
        <h3 className="text-xl md:text-2xl font-bold text-white">{title}</h3>
        {subtitle && <p className="text-stone-300 text-sm md:text-base mt-1">{subtitle}</p>}
      </div>
      {typeof count === 'number' && (
        <div className="text-stone-400 text-sm">{count} item{count === 1 ? '' : 's'}</div>
      )}
    </div>
    <div className="h-px bg-white/10 mt-4" />
  </div>
);

/**
 * Resource card that scales images predictably.
 * Uses <Link> for internal routes and <a> for external URLs / files.
 */
const ResourceCard = ({ resource }) => {
  const aspect = resource.aspect || '16/9';

  let objectPosition = resource.objectPosition;
  if (!objectPosition && typeof resource.focusX === 'number' && typeof resource.focusY === 'number') {
    objectPosition = `${resource.focusX}% ${resource.focusY}%`;
  }

  const fitClass = (resource.fit || 'cover') === 'contain' ? 'object-contain' : 'object-cover';

  const url = typeof resource.url === 'string' ? resource.url : '/';

  const stripQueryHash = (u) => u.split('#')[0].split('?')[0];
  const clean = stripQueryHash(url);

  const isHttp = /^https?:\/\//i.test(clean);
  const isPdf = /\.pdf$/i.test(clean);

  // Treat anything with a file extension, or anything served from /images/, as a static file link.
  const isFileLike =
    /\.(pdf|html?|jpg|jpeg|png|webp|gif|svg)$/i.test(clean) ||
    clean.startsWith('/images/');

  const useAnchor = isHttp || isFileLike;
  const Wrapper = useAnchor ? 'a' : Link;

  // PDFs open in a new tab (read without losing your place)
  const openInNewTab = isHttp || isPdf;

  const wrapperProps = useAnchor
    ? { href: url, ...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }
    : { to: url || '/' };

  return (
    <Wrapper
      {...wrapperProps}
      className="group relative bg-stone-700 rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.02]"
    >
      <div className="relative w-full" style={{ aspectRatio: aspect }}>
        <img
          src={resource.backgroundImage}
          alt={resource.title}
          className={`absolute inset-0 w-full h-full ${fitClass}`}
          style={{ objectPosition: objectPosition || '50% 50%', opacity: resource.backgroundOpacity ?? 1 }}
          onError={(e) => { e.currentTarget.src = '/images/default-thumbnail.jpg'; }}
          loading="lazy"
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-20 mix-blend-overlay
          bg-[radial-gradient(circle_at_20%_20%,_rgba(255,255,255,0.1)_0,_transparent_40%),
              radial-gradient(circle_at_80%_30%,_rgba(255,255,255,0.08)_0,_transparent_45%),
              radial-gradient(circle_at_40%_80%,_rgba(255,255,255,0.06)_0,_transparent_40%)]"
        />
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 via-black/30 to-transparent">
          <h3 className="text-white font-bold text-lg">{resource.title}</h3>
          {resource.description && <p className="text-stone-200 text-sm mt-2 line-clamp-2">{resource.description}</p>}
        </div>
      </div>
    </Wrapper>
  );
};

const ResourceGrid = ({ items }) => (
  <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-6">
    {items.map((res, idx) => (
      <ResourceCard key={res.id ?? `${res.title}-${idx}`} resource={res} />
    ))}
  </div>
);

const ControlsBar = ({
  search,
  setSearch,
  category,
  setCategory,
  categories,
  featuredOnly,
  setFeaturedOnly,
  total,
  shown,
}) => (
  <div className="max-w-6xl mx-auto px-1 mt-6 mb-2">
    <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
      <div className="flex-1">
        <label className="block text-stone-300 text-sm mb-2">Search</label>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search titles and descriptions…"
          className="w-full rounded-md bg-stone-800 border border-white/10 text-white px-3 py-2 outline-none focus:ring-2 focus:ring-orange-500/40"
        />
      </div>

      <div className="min-w-[220px]">
        <label className="block text-stone-300 text-sm mb-2">Category</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full rounded-md bg-stone-800 border border-white/10 text-white px-3 py-2 outline-none focus:ring-2 focus:ring-orange-500/40"
        >
          <option value="__all__">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3 md:pb-[6px]">
        <input
          id="featuredOnly"
          type="checkbox"
          checked={featuredOnly}
          onChange={(e) => setFeaturedOnly(e.target.checked)}
          className="h-4 w-4 accent-orange-500"
        />
        <label htmlFor="featuredOnly" className="text-stone-300 text-sm select-none">
          Featured only
        </label>
      </div>
    </div>

    <div className="text-stone-400 text-sm mt-3">
      Showing <span className="text-stone-200">{shown}</span> of <span className="text-stone-200">{total}</span>
    </div>
  </div>
);

/* --------------------- Organisation helpers --------------------- */
const safeText = (v) => (typeof v === 'string' ? v : '');
const normalise = (s) => safeText(s).trim().toLowerCase();

const getCategory = (r) => {
  if (r && typeof r.category === 'string' && r.category.trim()) return r.category.trim();
  const desc = safeText(r?.description);
  const parts = desc.split('—');
  if (parts.length >= 2) {
    const candidate = parts[0].trim();
    if (candidate.length > 0 && candidate.length <= 40) return candidate;
  }
  return 'Other';
};

const getFeatured = (items) => {
  const hasFlag = items.some((r) => r?.featured === true);
  if (hasFlag) return items.filter((r) => r?.featured === true);
  return items.slice(0, 6);
};

const matchesSearch = (r, q) => {
  if (!q) return true;
  const text = `${safeText(r?.title)} ${safeText(r?.description)} ${safeText(r?.url)}`.toLowerCase();
  return text.includes(q);
};

const groupByCategory = (items, order = []) => {
  const map = new Map();
  items.forEach((r) => {
    const c = getCategory(r);
    if (!map.has(c)) map.set(c, []);
    map.get(c).push(r);
  });

  for (const [k, arr] of map.entries()) {
    arr.sort((a, b) => safeText(a.title).localeCompare(safeText(b.title)));
  }

  const ordered = [];
  const seen = new Set();

  order.forEach((c) => {
    if (map.has(c)) {
      ordered.push([c, map.get(c)]);
      seen.add(c);
    }
  });

  const rest = [...map.keys()].filter((k) => !seen.has(k)).sort((a, b) => a.localeCompare(b));
  rest.forEach((c) => ordered.push([c, map.get(c)]));

  return ordered;
};

/* --------------------- Other existing components (kept) --------------------- */
const MediaTile = ({ item, onImageClick, featured = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaLabel = item.title || item.alt || item.collection || 'Roman Storyworld media';

  const isTouchDevice =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const handleClick = () => {
    if (item.type === 'image') onImageClick && onImageClick(item);
    else if (item.type === 'video') {
      const videoEl = document.getElementById(`video-${item.id}`);
      if (!videoEl) return;
      if (videoEl.paused) {
        videoEl.muted = false;
        videoEl.play();
        setIsPlaying(true);
      } else {
        videoEl.pause();
        videoEl.muted = true;
        setIsPlaying(false);
      }
    }
  };

  return (
    <button
      type="button"
      className={`group relative block w-full overflow-hidden rounded-2xl border border-amber-200/10 bg-stone-950 text-left shadow-2xl shadow-black/30 transition-all duration-500 hover:-translate-y-1 hover:border-amber-300/40 hover:shadow-amber-900/20 focus:outline-none focus:ring-2 focus:ring-amber-400/50 ${featured ? 'md:col-span-2 lg:col-span-2' : ''}`}
      onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
      onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
      onClick={handleClick}
      aria-label={`${item.type === 'video' ? 'Play' : 'Open'} ${mediaLabel}`}
    >
      <div className={`relative ${featured ? 'aspect-[16/9] md:aspect-[21/9]' : 'aspect-video'}`}>
        {item.type === 'video' ? (
          <video
            id={`video-${item.id}`}
            className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
            src={item.videoUrl}
            muted
            loop
            playsInline
            poster={item.thumbnail}
            preload="metadata"
            {...(!isTouchDevice && isHovered ? { autoPlay: true } : {})}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        ) : (
          <img
            src={item.thumbnail}
            alt={item.alt || item.title || item.collection || 'Roman Storyworld media image'}
            className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
            onError={(e) => { e.currentTarget.src = '/images/default-thumbnail.jpg'; }}
            loading="lazy"
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
        <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-[radial-gradient(circle_at_50%_20%,rgba(251,191,36,0.18),transparent_35%),radial-gradient(circle_at_20%_90%,rgba(120,53,15,0.35),transparent_40%)]" />
        <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/50 to-transparent" />

        {item.type === 'video' && (
          <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${isPlaying ? 'opacity-0' : 'opacity-100'}`}>
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-black/60 shadow-xl backdrop-blur-sm transition duration-300 group-hover:scale-110 group-hover:bg-amber-500/80">
              <svg className="ml-1 h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        )}

        <div className="absolute left-4 top-4 flex items-center gap-2">
          <span className="rounded-full border border-white/15 bg-black/45 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-100 backdrop-blur-sm">
            {item.type === 'video' ? 'Film' : 'Image'}
          </span>
          {item.duration && (
            <span className="rounded-full border border-white/15 bg-black/45 px-3 py-1 text-[11px] font-semibold text-stone-100 backdrop-blur-sm">
              {item.duration}
            </span>
          )}
        </div>

        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
          {item.collection && (
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-amber-200/90">
              {item.collection}
            </p>
          )}
          <h3 className={`${featured ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'} font-bold leading-tight text-white drop-shadow`}>
            {item.title}
          </h3>
          {item.description && (
            <p className={`${featured ? 'mt-3 max-w-2xl text-base' : 'mt-2 text-sm'} line-clamp-2 text-stone-200`}>
              {item.description}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

const ImageModal = ({ image, isOpen, onClose }) => {
  if (!isOpen || !image) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/92 p-3 md:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="mx-auto flex h-full max-w-6xl items-center justify-center">
        <div
          className="relative flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-2xl border border-amber-200/20 bg-stone-950 shadow-2xl md:max-h-[calc(100vh-3rem)]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute right-3 top-3 z-20 rounded-full border border-white/20 bg-black/70 px-3 py-2 text-white backdrop-blur-sm transition hover:bg-amber-600 md:right-4 md:top-4"
            aria-label="Close image"
          >
            ✕
          </button>

          <div className="flex min-h-0 flex-1 items-center justify-center bg-black/35 p-2 md:p-4">
            <img
              src={image.fullImage || image.thumbnail}
              alt={image.alt || image.title || image.collection || 'Roman Storyworld media image'}
              className="block max-h-[62vh] max-w-full object-contain md:max-h-[68vh]"
            />
          </div>

          <div className="shrink-0 bg-gradient-to-t from-black to-stone-950/90 p-4 md:p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-200">Visual Archive</p>
            <h3 className="mt-2 text-xl font-bold text-white md:text-2xl">{image.title}</h3>
            {image.description && <p className="mt-2 text-sm leading-relaxed text-stone-300 md:text-base">{image.description}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

const MediaSection = ({ title, subtitle, items, onImageClick }) => (
  <section className="mx-auto mt-12 max-w-7xl px-4 md:px-6">
    <div className="mb-5 flex flex-col justify-between gap-3 border-b border-amber-200/10 pb-4 md:flex-row md:items-end">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/80">Roman Storyworld</p>
        <h2 className="mt-2 text-2xl font-bold text-white md:text-3xl">{title}</h2>
        {subtitle && <p className="mt-2 max-w-3xl text-sm leading-relaxed text-stone-300 md:text-base">{subtitle}</p>}
      </div>
      <span className="text-sm text-stone-400">{items.length} archive piece{items.length === 1 ? '' : 's'}</span>
    </div>

    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
      {items.map((item, index) => (
        <MediaTile
          key={item.id}
          item={item}
          onImageClick={onImageClick}
          featured={index === 0 && items.length > 2}
        />
      ))}
    </div>
  </section>
);

const MediaHero = ({ featuredItem, onImageClick }) => (
  <section className="relative bg-stone-900">
    <div className="mx-auto max-w-7xl px-6 py-20 text-center md:py-28">
      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-amber-300">Images and Video</p>
      <h1 className="mt-4 text-4xl font-black tracking-tight text-white md:text-6xl">Sons of the Standard</h1>
      <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-stone-200 md:text-lg">
        Explore the visual world of the Sons of the Standard trilogy: story films, character portraits, landscapes, battle scenes, maps, and reconstructions inspired by Roman Britain. The past survives in fragments — firelight, water, iron, oath, and the faces of those who refused to be forgotten.
      </p>
      <div className="mx-auto mt-8 max-w-4xl">
        <MediaTile item={featuredItem} onImageClick={onImageClick} featured />
      </div>
    </div>
  </section>
);


const MediaArchiveIntro = () => (
  <section className="mx-auto mt-10 max-w-5xl px-6" aria-labelledby="media-archive-intro">
    <div className="rounded-2xl border border-amber-200/10 bg-stone-950/45 px-6 py-6 text-center md:px-8">
      <h2 id="media-archive-intro" className="text-2xl font-bold text-white md:text-3xl">
        Roman Storyworld Media Archive
      </h2>
      <p className="mx-auto mt-3 max-w-4xl text-sm leading-relaxed text-stone-300 md:text-base">
        This archive brings together the films and images created around Roman Storyworld and the completed Sons of the Standard trilogy. It follows Gaius Marcellus Varro and the people around him through Roman Britain, from Deva Victrix and the western mountains to roads, rivers, sacred places, warfare, ritual, and the landscapes that shape the story. Use the archive alongside the books, character pages, evidence-led investigations, and interactive maps to move between fiction, historical setting, and the research beneath the storyworld.
      </p>
      <nav className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm" aria-label="Explore Roman Storyworld">
        <Link to="/books" className="font-semibold text-amber-200 transition hover:text-amber-100">Books</Link>
        <Link to="/characters" className="font-semibold text-amber-200 transition hover:text-amber-100">Characters</Link>
        <Link to="/investigations" className="font-semibold text-amber-200 transition hover:text-amber-100">Investigations</Link>
        <Link to="/maps" className="font-semibold text-amber-200 transition hover:text-amber-100">Interactive Maps</Link>
      </nav>
    </div>
  </section>
);

const StoryFilmSection = ({ films }) => (
  <section className="mx-auto mt-12 max-w-7xl px-4 md:px-6">
    <div className="mb-5 border-b border-amber-200/10 pb-4">
      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/80">Roman Storyworld</p>
      <h2 className="mt-2 text-2xl font-bold text-white md:text-3xl">Story Films</h2>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-stone-300 md:text-base">
        Illustrated retellings of the people, conflicts, rituals, and turning points within the Sons of the Standard trilogy.
      </p>
    </div>

    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {films.map((film) => (
        <article
          key={film.id}
          className="flex h-full flex-col overflow-hidden rounded-2xl border border-amber-200/15 bg-stone-950 shadow-2xl shadow-black/30"
        >
          <div className="relative aspect-[16/9] shrink-0 overflow-hidden bg-black">
            <video
              className="absolute inset-0 block h-full w-full object-cover"
              src={film.videoUrl}
              poster={film.thumbnail}
              controls
              playsInline
              preload="metadata"
              aria-label={film.title}
            />
            
          </div>

          <div className="flex flex-1 flex-col p-5 md:p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-200/90">{film.collection}</p>
            <h3 className="mt-2 text-2xl font-bold text-white">{film.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-stone-300 md:text-base">{film.description}</p>
            {film.bookUrl && (
              <div className="mt-auto pt-5">
                <Link
                  to={film.bookUrl}
                  className="inline-flex items-center rounded-full border border-amber-300/30 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-100 transition hover:border-amber-300/60 hover:bg-amber-500/20"
                >
                  Discover the book
                </Link>
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
  </section>
);

/* --------------------- Pages --------------------- */
const Characters = () => (
  <div className="min-h-screen bg-stone-900 py-0 px-6">
    <HeaderSection
      size="sm"
      title="Characters"
      subtitle="Dramatis Personae"
      blurb="Ask about any character—relationships, motives, rituals, oaths—and I’ll answer from the archive."
    />

    {/* Chatbot temporarily disabled (toggle ENABLE_CHAR_CHAT to true to re-enable) */}
    {ENABLE_CHAR_CHAT && (
      <FloatingChat
        key="/api/characters/chat"
        title="Characters Chat"
        apiPath="/api/characters/chat"
        storageKey="characters"
        placeholder="Ask a question"
      />
    )}
  </div>
);

const Media = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);


  // Story-film files:
  // public/videos/story-films/blood-and-honour-story-film.mp4
  // public/videos/story-films/shadows-of-the-eagle-story-film.mp4
  // public/videos/story-films/sons-of-the-standard-story-film.mp4
  // public/images/story-films/blood-and-honour-arena.jpg
  // public/images/story-films/the-running-boar.jpg
  // public/images/story-films/aerwyna-victory.jpg
  const storyFilmItems = [
    {
      id: 'story-film-blood-and-honour',
      type: 'story-film',
      collection: 'Story Film · Book I',
      label: 'Book I',
      title: 'Blood and Honour',
      thumbnail: '/images/story-films/blood-and-honour-arena.jpg',
      videoUrl: '/videos/story-films/blood-and-honour-story-film.mp4',
      duration: '1:41',
      description: 'Defeated at Lugdunum and condemned to the arena at Deva, Varro must survive Rome’s judgement, confront the men who betrayed him, and begin the long road from disgraced centurion to leader of the Running Boar.',
      bookUrl: '/books',
    },
    {
      id: 'story-film-shadows',
      type: 'story-film',
      collection: 'Story Film · Book II',
      label: 'Book II',
      title: 'Shadows of the Eagle',
      thumbnail: '/images/story-films/the-running-boar.jpg',
      videoUrl: '/videos/story-films/shadows-of-the-eagle-story-film.mp4',
      duration: '1:34',
      description: 'The Eagle has become a burden. Across mountain, mine, and treacherous sea, Varro, Aerwyna, and their allies must choose a new standard—and decide what they are prepared to defend.',
      bookUrl: '/books',
    },
    {
      id: 'story-film-sons',
      type: 'story-film',
      collection: 'Story Film · Book III',
      label: 'Book III',
      title: 'Sons of the Standard',
      thumbnail: '/images/story-films/aerwyna-victory.jpg',
      videoUrl: '/videos/story-films/sons-of-the-standard-story-film.mp4',
      duration: '1:41',
      description: 'Britannia stands between fire and sea. Old enemies close in as Varro, Aerwyna, Lucius, and the Running Boar alliance prepare to defend their people and determine what survives Rome.',
      bookUrl: '/books',
    },
  ];

  // Put these files in: public/images/trilogy images/
  // Website paths then become: /images/trilogy images/<filename>.png
  const trilogyImageBase = '/images/trilogy images';

  const trilogyPromoItems = [
    {
      id: 'trilogy-01',
      type: 'image',
      alt: 'Sons of the Standard trilogy promotional artwork for the Roman Storyworld historical fiction series',
      collection: 'Trilogy Promotion',
      title: '',
      thumbnail: `${trilogyImageBase}/01-trilogy-promo.png`,
      fullImage: `${trilogyImageBase}/01-trilogy-promo.png`,
      description: '',
    },
    {
      id: 'trilogy-03',
      type: 'image',
      alt: 'Blood and Honour promotional artwork from the Sons of the Standard trilogy',
      collection: 'Trilogy Promotion',
      title: '',
      thumbnail: `${trilogyImageBase}/03-blood-and-honour-promo.png`,
      fullImage: `${trilogyImageBase}/03-blood-and-honour-promo.png`,
      description: '',
    },
    {
      id: 'trilogy-05',
      type: 'image',
      alt: 'Shadows of the Eagle promotional artwork from the Sons of the Standard trilogy',
      collection: 'Trilogy Promotion',
      title: '',
      thumbnail: `${trilogyImageBase}/05-shadows-of-the-eagle-promo.png`,
      fullImage: `${trilogyImageBase}/05-shadows-of-the-eagle-promo.png`,
      description: '',
    },
    {
      id: 'trilogy-07',
      type: 'image',
      alt: 'Sons of the Standard Book III promotional artwork from the Roman Storyworld trilogy',
      collection: 'Trilogy Promotion',
      title: '',
      thumbnail: `${trilogyImageBase}/07-sons-of-the-standard-promo.png`,
      fullImage: `${trilogyImageBase}/07-sons-of-the-standard-promo.png`,
      description: '',
    },
    {
      id: 'trilogy-02',
      type: 'image',
      alt: 'Roman Storyworld map and setting artwork showing the world of the Sons of the Standard trilogy',
      collection: 'Storyworld and Setting',
      title: '',
      thumbnail: `${trilogyImageBase}/02-storyworld-map-promo.png`,
      fullImage: `${trilogyImageBase}/02-storyworld-map-promo.png`,
      description: '',
    },
    {
      id: 'trilogy-10',
      type: 'image',
      alt: 'Roman Britain land sea and legacy artwork for the Sons of the Standard storyworld',
      collection: 'Storyworld and Setting',
      title: '',
      thumbnail: `${trilogyImageBase}/10-world-land-sea-legacy.png`,
      fullImage: `${trilogyImageBase}/10-world-land-sea-legacy.png`,
      description: '',
    },
    {
      id: 'trilogy-04',
      type: 'image',
      alt: 'Blood and Honour key scenes artwork from the Sons of the Standard trilogy',
      collection: 'Trilogy Key Scenes',
      title: '',
      thumbnail: `${trilogyImageBase}/04-blood-and-honour-key-scenes.png`,
      fullImage: `${trilogyImageBase}/04-blood-and-honour-key-scenes.png`,
      description: '',
    },
    {
      id: 'trilogy-06',
      type: 'image',
      alt: 'Shadows of the Eagle key scenes artwork from the Sons of the Standard trilogy',
      collection: 'Trilogy Key Scenes',
      title: '',
      thumbnail: `${trilogyImageBase}/06-shadows-of-the-eagle-key-scenes.png`,
      fullImage: `${trilogyImageBase}/06-shadows-of-the-eagle-key-scenes.png`,
      description: '',
    },
    {
      id: 'trilogy-08',
      type: 'image',
      alt: 'Sons of the Standard Book III key scenes artwork from the completed trilogy',
      collection: 'Trilogy Key Scenes',
      title: '',
      thumbnail: `${trilogyImageBase}/08-sons-of-the-standard-key-scenes.png`,
      fullImage: `${trilogyImageBase}/08-sons-of-the-standard-key-scenes.png`,
      description: '',
    },
    {
      id: 'trilogy-09',
      type: 'image',
      alt: 'Key characters promotional artwork for the Sons of the Standard historical fiction trilogy',
      collection: 'Characters and Adaptation',
      title: '',
      thumbnail: `${trilogyImageBase}/09-key-characters-promo.png`,
      fullImage: `${trilogyImageBase}/09-key-characters-promo.png`,
      description: '',
    },
    {
      id: 'trilogy-11',
      type: 'image',
      alt: 'Storyboard artwork depicting the surrender of the eagle in the Sons of the Standard storyworld',
      collection: 'Characters and Adaptation',
      title: '',
      thumbnail: `${trilogyImageBase}/11-storyboard-surrender-of-the-eagle.png`,
      fullImage: `${trilogyImageBase}/11-storyboard-surrender-of-the-eagle.png`,
      description: '',
    },
    {
      id: 'trilogy-12',
      type: 'image',
      alt: 'Roman Storyworld adaptation promotional artwork presenting the Sons of the Standard trilogy as an epic for today',
      collection: 'Characters and Adaptation',
      title: '',
      thumbnail: `${trilogyImageBase}/12-epic-for-today-promo.png`,
      fullImage: `${trilogyImageBase}/12-epic-for-today-promo.png`,
      description: '',
    },
  ];

  const existingMediaItems = [
    { id: 1, type: 'image', collection: 'Featured Vision', title: 'Varro, Roman Officer, now Slave', thumbnail: '/images/gladiator_warrior_defeated.jpg', fullImage: '/images/gladiator_warrior_defeated.jpg', description: 'He must find a path to redemption.' },
    { id: 2, type: 'video', collection: 'Character Vision', title: 'Aerwyna: Moment of Joy', thumbnail: '/thumbnails/aerwyna.jpg', videoUrl: '/videos/aerwyna_character_segment.mp4', duration: '0:30', description: 'Aerwyna makes an offering to the river gods, her prayer is answered.' },
    { id: 3, type: 'image', collection: 'Rome at Sea', title: 'Roman Naval Fleet', thumbnail: '/images/roman_naval_fleet.jpg', fullImage: '/images/roman_naval_fleet.jpg', description: 'Spectacular scene as the Roman fleet leaves the coast of Britannia. Many men will not return.' },
    { id: 4, type: 'video', collection: 'Craft and Survival', title: 'The Celtic Blacksmith', thumbnail: '/thumbnails/blacksmith.jpg', videoUrl: '/videos/blacksmith_at_work.mp4', duration: '0:35', description: 'The blacksmith repairing weapons and armour.' },
    { id: 5, type: 'image', collection: 'Warfare and Fire', title: 'Siege of Tomen-y-Mur', thumbnail: '/images/siege_battle_fire.jpg', fullImage: '/images/siege_battle_fire.jpg', description: 'Dramatic siege scene with fire arrows and fortress assault, capturing the intensity of the Dacian assault to capture the eagle of the Twentieth.' },
    { id: 6, type: 'video', collection: 'Sacred Britannia', title: 'Northern Lights Over Britain', thumbnail: '/thumbnails/aurora.jpg', videoUrl: '/videos/aurora_mountains_night.mp4', duration: '0:20', description: 'Mystical aurora borealis over the British mountains, saving Varro and his men from a Dacian night assault.' },
    { id: 7, type: 'image', collection: 'Standards and Oaths', title: 'Standard Bearer of Valeria', thumbnail: '/images/valeria_standard_bearer.jpg', fullImage: '/images/valeria_standard_bearer.jpg', description: 'Soldiers of the twentieth legion rally to the standard of the running Boar.' },
    { id: 8, type: 'video', collection: 'Warfare and Fire', title: 'Naval Battle Scene', thumbnail: '/thumbnails/battle.jpg', videoUrl: '/videos/naval_battle_scene.mp4', duration: '1:15', description: 'Varro and his men barely escape the Roman Harbour of Deva.' },
    { id: 9, type: 'image', collection: 'Character Vision', title: 'Cunobara: The Voice of the Bones', thumbnail: '/images/cunobara_druid_portrait.jpg', fullImage: '/images/cunobara_druid_portrait.jpg', description: 'Celtic druid elder, the living link between past and present, guardian of ancient wisdom.' },
    { id: 10, type: 'video', collection: 'Cities in Ash', title: 'Fall of Viriconium', thumbnail: '/thumbnails/viriconium.jpg', videoUrl: '/videos/viriconium_destruction.mp4', duration: '0:30', description: 'The dramatic destruction of the Roman city of Viriconium by the Brigantes.' },
    { id: 11, type: 'image', collection: 'Character Vision', title: 'Julius Juba: Warrior and Leader', thumbnail: '/images/julius_juba_portrait.jpg', fullImage: '/images/julius_juba_portrait.jpg', description: 'Steadfast and ready to die for his men, standing in the shadow of Eryri, where the eagle soars.' },
    { id: 12, type: 'video', collection: 'Landscape and Memory', title: 'The Author of Sons of the Standard Series Books', thumbnail: '/thumbnails/author.jpg', videoUrl: '/videos/cliff_atmospheric.mp4', duration: '0:25', description: 'Dramatic coastal cliffs, with atmospheric lighting, showcasing the rugged British landscape.' },
    { id: 13, type: 'image', collection: 'Character Vision', title: 'Cornovii Warrior Leader: Painted for Battle', thumbnail: '/images/celtic_warrior_painted.jpg', fullImage: '/images/celtic_warrior_painted.jpg', description: 'This land is ours, Maelgwn said, voice like stone breaking.' },
    { id: 14, type: 'image', collection: 'Landscape and Memory', title: 'Wild Horses of the Ordovices', thumbnail: '/images/british_horses_landscape.jpg', fullImage: '/images/british_horses_landscape.jpg', description: 'Horses grazing beneath the Western Mountains, revered by Celtic people.' },
    { id: 15, type: 'image', collection: 'Landscape and Memory', title: 'Warrior in the Marshlands', thumbnail: '/images/warrior_marshlands.jpg', fullImage: '/images/warrior_marshlands.jpg', description: 'Lucius Cunovalos wading through reedy marshes, showing the challenging terrain of Roman Britain.' },
    { id: 16, type: 'video', collection: 'Warfare and Fire', title: 'Battle of Lugdunum', thumbnail: '/thumbnails/lugdunum.jpg', videoUrl: '/videos/lugdunum.mp4', duration: '0:20', description: 'The Standard of Legion XX falls.' },
    { id: 17, type: 'video', collection: 'Arena and Chains', title: 'The Arena at Deva', thumbnail: '/thumbnails/gladiator.jpg', videoUrl: '/videos/gladiator.mp4', duration: '0:10', description: 'Varro and the Emperor’s Champion, a clash of steel.' },
    { id: 18, type: 'video', collection: 'The Twentieth', title: 'Julius, Second in Command', thumbnail: '/thumbnails/julius.jpg', videoUrl: '/videos/centurion.mp4', duration: '0:10', description: 'The men of the Twentieth prepare to face the Dacians.' },
  ];

  const mediaItems = [...trilogyPromoItems, ...existingMediaItems];

  const sections = [
    {
      title: 'Featured Promotional Images',
      subtitle: 'Series artwork, book promotion, and visual storyworld material for the completed trilogy.',
      items: mediaItems.filter((item) => item.collection === 'Trilogy Promotion'),
    },
    {
      title: 'Storyworld and Setting',
      subtitle: 'Maps, landscapes, roads, borders, sacred places, and the wider world beneath the story.',
      items: mediaItems.filter((item) => item.collection === 'Storyworld and Setting'),
    },
    {
      title: 'Trilogy Key Scenes',
      subtitle: 'Compact visual summaries of the central scenes and turning points across the three books.',
      items: mediaItems.filter((item) => item.collection === 'Trilogy Key Scenes'),
    },
    {
      title: 'Characters and Adaptation',
      subtitle: 'Character panels, storyboard material, and adaptation-facing artwork for the series.',
      items: mediaItems.filter((item) => item.collection === 'Characters and Adaptation'),
    },
    {
      title: 'Character Visions',
      subtitle: 'Faces, loyalties, and private moments from the Sons of the Standard storyworld.',
      items: mediaItems.filter((item) => ['Character Vision', 'Featured Vision', 'Arena and Chains', 'The Twentieth'].includes(item.collection)),
    },
    {
      title: 'Warfare, Fire, and Escape',
      subtitle: 'Sieges, sea battles, collapsing cities, and the moments when survival becomes command.',
      items: mediaItems.filter((item) => ['Warfare and Fire', 'Cities in Ash', 'Rome at Sea', 'Standards and Oaths'].includes(item.collection)),
    },
    {
      title: 'Land, Craft, and Sacred Memory',
      subtitle: 'Britannia as witness: rivers, mountains, craft, animals, omen, and weathered coastline.',
      items: mediaItems.filter((item) => ['Landscape and Memory', 'Craft and Survival', 'Sacred Britannia'].includes(item.collection)),
    },
  ].filter((section) => section.items.length > 0);

  const handleImageClick = (image) => { setSelectedImage(image); setIsModalOpen(true); };
  const handleModalClose = () => { setIsModalOpen(false); setSelectedImage(null); };
  const heroItem = trilogyPromoItems[0] || mediaItems[0];

  return (
    <div className="min-h-screen overflow-hidden bg-stone-900">
      <MediaHero featuredItem={heroItem} onImageClick={handleImageClick} />
      <MediaArchiveIntro />

      <div className="relative bg-stone-900 pb-16">
        <div className="relative">
          <StoryFilmSection films={storyFilmItems} />

          {sections.map((section) => (
            <MediaSection
              key={section.title}
              title={section.title}
              subtitle={section.subtitle}
              items={section.items}
              onImageClick={handleImageClick}
            />
          ))}
        </div>
      </div>

      <ImageModal image={selectedImage} isOpen={isModalOpen} onClose={handleModalClose} />
    </div>
  );
};


const Timeline = () => <div className="p-8 text-center">Timeline page coming soon.</div>;

/* --------------------- Organised hubs --------------------- */
const Research = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('__all__');
  const [featuredOnly, setFeaturedOnly] = useState(false);

  const categoryOrder = [
    'Core Reference Portals',
    'Conquest & Conflict',
    'Sites, Reports & Archaeology',
    'Roads & Movement',
    'Belief & Ritual',
    'Settlements & Economy',
    'Military & Society',
    'Other',
  ];

  const organised = useMemo(() => {
    const items = Array.isArray(resources) ? resources : [];
    const q = normalise(search);

    const featured = getFeatured(items);

    const filtered = items.filter((r) => matchesSearch(r, q));

    const filteredByCat =
      category === '__all__'
        ? filtered
        : filtered.filter((r) => getCategory(r) === category);

    const featuredSet = new Set(featured.map((r) => r?.id ?? r?.title));
    const featuredFiltered = filteredByCat.filter((r) => featuredSet.has(r?.id ?? r?.title));
    const restFiltered = filteredByCat.filter((r) => !featuredSet.has(r?.id ?? r?.title));

    const grouped = groupByCategory(restFiltered, categoryOrder);

    const categories = Array.from(new Set(items.map(getCategory))).filter(Boolean).sort((a, b) => a.localeCompare(b));

    const shownCount = (featuredOnly ? featuredFiltered.length : featuredFiltered.length + restFiltered.length);

    return {
      total: items.length,
      shown: shownCount,
      categories,
      featured: featuredFiltered,
      grouped,
    };
  }, [search, category, featuredOnly]);

  return (
    <div className="min-h-screen bg-stone-900 py-0 px-6 pb-14">
      <ResearchSchema />

      <HeaderSection
        size="sm"
        title="Research Resources"
        subtitle="Supporting Studies"
        blurb="Explore the academic and historical resources that informed Blood and Honour."
      />

      <ControlsBar
        search={search}
        setSearch={setSearch}
        category={category}
        setCategory={setCategory}
        categories={organised.categories}
        featuredOnly={featuredOnly}
        setFeaturedOnly={setFeaturedOnly}
        total={organised.total}
        shown={organised.shown}
      />

      {organised.featured.length > 0 && (
        <>
          <SectionHeading title="Featured" subtitle="Best starting points and storyworld anchors" count={organised.featured.length} />
          <ResourceGrid items={organised.featured} />
        </>
      )}

      {!featuredOnly && organised.grouped.map(([cat, items]) => (
        <div key={cat}>
          <SectionHeading title={cat} count={items.length} />
          <ResourceGrid items={items} />
        </div>
      ))}

      {ENABLE_RESEARCH_CHAT && (
        <FloatingChat
          key="/api/research/chat"
          title="Research Assistant"
          apiPath="/api/research/chat"
          storageKey="research"
          placeholder="Ask a question"
          extraBody={{ threshold: 0.35, count: 12 }}
        />
      )}
    </div>
  );
};

const Maps = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('__all__');
  const [featuredOnly, setFeaturedOnly] = useState(false);

  const categoryOrder = [
    'Deva & Cheshire Focus',
    'Legions & Military',
    'Roads & Routes',
    'Tribes & Iron Age Landscapes',
    'Settlements & Economy',
    'Logistics & Resources',
    'Atlases & Reference Maps',
    'Other',
  ];

  const organised = useMemo(() => {
    const items = Array.isArray(mapResources) ? mapResources : [];
    const q = normalise(search);

    const featured = getFeatured(items);

    const filtered = items.filter((r) => matchesSearch(r, q));

    const filteredByCat =
      category === '__all__'
        ? filtered
        : filtered.filter((r) => getCategory(r) === category);

    const featuredSet = new Set(featured.map((r) => r?.id ?? r?.title));
    const featuredFiltered = filteredByCat.filter((r) => featuredSet.has(r?.id ?? r?.title));
    const restFiltered = filteredByCat.filter((r) => !featuredSet.has(r?.id ?? r?.title));

    const grouped = groupByCategory(restFiltered, categoryOrder);

    const categories = Array.from(new Set(items.map(getCategory))).filter(Boolean).sort((a, b) => a.localeCompare(b));

    const shownCount = (featuredOnly ? featuredFiltered.length : featuredFiltered.length + restFiltered.length);

    return {
      total: items.length,
      shown: shownCount,
      categories,
      featured: featuredFiltered,
      grouped,
    };
  }, [search, category, featuredOnly]);

  return (
    <div className="min-h-screen bg-stone-900 py-0 px-6 pb-14">
      <HeaderSection
        size="sm"
        title="Interactive Maps"
        subtitle="Explore & Visualise"
        blurb="All the map experiences used in Roman Storyworld and my research."
      />

      <ControlsBar
        search={search}
        setSearch={setSearch}
        category={category}
        setCategory={setCategory}
        categories={organised.categories}
        featuredOnly={featuredOnly}
        setFeaturedOnly={setFeaturedOnly}
        total={organised.total}
        shown={organised.shown}
      />

      {organised.featured.length > 0 && (
        <>
          <SectionHeading title="Featured" subtitle="Start here for the core map experiences" count={organised.featured.length} />
          <ResourceGrid items={organised.featured} />
        </>
      )}

      {!featuredOnly && organised.grouped.map(([cat, items]) => (
        <div key={cat}>
          <SectionHeading title={cat} count={items.length} />
          <ResourceGrid items={items} />
        </div>
      ))}
    </div>
  );
};

/* --------------------- Characters dropdown archive pages --------------------- */

const StoryworldCarousel = ({ items = [], labelForItem, title, subtitle, blurb }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = items[activeIndex] || items[0];

  const previous = () => setActiveIndex((i) => (i - 1 + items.length) % items.length);
  const next = () => setActiveIndex((i) => (i + 1) % items.length);

  if (!active) return null;

  const label = labelForItem ? labelForItem(active) : active.region || active.tag || 'Storyworld';

  return (
    <div className="min-h-screen bg-stone-950 py-0 px-6 pb-16 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(circle_at_18%_12%,_rgba(251,191,36,0.18)_0,_transparent_30%),radial-gradient(circle_at_78%_18%,_rgba(249,115,22,0.14)_0,_transparent_34%),linear-gradient(180deg,_rgba(255,255,255,0.04)_0,_transparent_22%,_rgba(0,0,0,0.35)_100%)]" />
      <div className="relative z-10">
        <HeaderSection size="sm" title={title} subtitle={subtitle} blurb={blurb} />

        <div className="max-w-6xl mx-auto mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-7 items-stretch">
            <div className="relative rounded-2xl overflow-hidden border border-amber-200/20 bg-black shadow-2xl shadow-black/40 min-h-[260px] md:min-h-[420px]">
              <img
                src={active.image}
                alt={active.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
                onError={(e) => { e.currentTarget.src = '/images/default-thumbnail.jpg'; }}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/10" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-[11px] tracking-wide uppercase bg-amber-300/10 border border-amber-200/25 text-amber-100">
                  {label}
                </div>
                <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white tracking-wide">
                  {active.name}
                </h2>
                {active.subtitle && (
                  <p className="mt-3 text-sm md:text-base text-stone-200 leading-relaxed max-w-xl">
                    {active.subtitle}
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200/20 bg-gradient-to-br from-stone-950 via-stone-900 to-black shadow-2xl shadow-black/40 overflow-hidden">
              <div className="relative p-6 md:p-7">
                <div className="absolute inset-0 pointer-events-none opacity-35 bg-[radial-gradient(circle_at_18%_8%,_rgba(251,191,36,0.22)_0,_transparent_28%),radial-gradient(circle_at_82%_20%,_rgba(249,115,22,0.14)_0,_transparent_32%)]" />
                <div className="relative">
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div>
                      <p className="text-xs uppercase tracking-[0.22em] text-amber-200/80">Archive entry</p>
                      <h3 className="mt-1 text-xl md:text-2xl font-bold text-amber-50">{active.name}</h3>
                    </div>
                    <div className="text-sm text-stone-400 whitespace-nowrap">
                      {activeIndex + 1} / {items.length}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {active.blocks?.map((block) => (
                      <section key={block.title} className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                        <h4 className="text-xs font-bold uppercase tracking-wide text-amber-200">{block.title}</h4>
                        <p className="mt-2 text-sm text-stone-300 leading-relaxed">{block.text}</p>
                      </section>
                    ))}
                  </div>

                  {!!active.keyPeople?.length && (
                    <section className="mt-4 rounded-xl border border-white/10 bg-white/[0.035] p-4">
                      <h4 className="text-xs font-bold uppercase tracking-wide text-amber-200">Key People</h4>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {active.keyPeople.map((person) => (
                          <span key={person} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-black/30 border border-amber-200/15 text-stone-200">
                            {person}
                          </span>
                        ))}
                      </div>
                    </section>
                  )}

                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex gap-3">
                      <button type="button" onClick={previous} className="px-4 py-2 rounded-full border border-amber-200/25 bg-black/30 text-amber-100 text-sm hover:bg-amber-200/10 transition-colors">
                        ← Previous
                      </button>
                      <button type="button" onClick={next} className="px-4 py-2 rounded-full border border-amber-200/25 bg-black/30 text-amber-100 text-sm hover:bg-amber-200/10 transition-colors">
                        Next →
                      </button>
                    </div>
                    <div className="flex gap-2 justify-center sm:justify-end">
                      {items.map((item, idx) => (
                        <button
                          key={item.id || item.name}
                          type="button"
                          onClick={() => setActiveIndex(idx)}
                          aria-label={`Show ${item.name}`}
                          className={`h-2.5 rounded-full transition-all ${idx === activeIndex ? 'w-8 bg-amber-300' : 'w-2.5 bg-white/25 hover:bg-white/45'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {items.map((item, idx) => (
              <button
                key={item.id || item.name}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className={`group relative rounded-xl overflow-hidden border text-left min-h-[92px] transition-all ${idx === activeIndex ? 'border-amber-300/70 shadow-lg shadow-orange-950/30' : 'border-white/10 hover:border-amber-200/40'}`}
              >
                <img src={item.image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="relative z-10 p-3 flex h-full items-end">
                  <span className="text-sm font-bold text-white leading-tight">{item.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const TRIBES = [
  {
    id: 'cornovii',
    name: 'CORNOVII',
    region: 'Midlands',
    image: '/images/tribes/cornovii.webp',
    subtitle:
      'Riverlands and border country — a counted land where obligations travel faster than armies.',
    blocks: [
      { title: 'The Land', text: 'Where roads converge and rivers slow: fertile ground watched by hillfort silhouettes and Roman stone laid like scars across older earth.' },
      { title: 'The People (after 196 CE)', text: 'Divided. Some prosper under Rome’s shadow; others hold to older customs and wait for the land to speak again. Patient — and never forgetting debt.' },
      { title: 'Story Tension', text: 'Bound by obligation: money owed, lives spared, promises made under duress. To rebel risks annihilation; to cling to Rome risks future shame.' },
      { title: 'Ritual & Obligation', text: 'Hospitality is sacred. Oaths at stones older than any hall. Blood spilled under false welcome stains more than earth — it awakens memory.' },
      { title: 'What They Want', text: 'Time — to see the wind’s direction, to choose which debts are paid, and which will never be honoured again.' },
    ],
    keyPeople: [
      'The Chieftain — burdened by debt',
      'The Champion — cost of alliance',
      'Deal-makers — soft voices, sharp sums',
      'Watchers — beacons, rumours, old paths',
    ],
  },
  {
    id: 'ordovices',
    name: 'ORDOVICES',
    region: 'North & Central Wales',
    image: '/images/tribes/ordovices.webp',
    subtitle:
      'High ground and hard choices — a people who listen to wind, river, and omen before they move.',
    blocks: [
      { title: 'The Land', text: 'Ridges, passes, storm-birth valleys. Roads falter here; Roman order thins. Springs break from stone where the earth seems to breathe.' },
      { title: 'The People (after 196 CE)', text: 'Survivors who refuse to vanish. Kinship and memory bind them: burned valleys, taken leaders, missing children. Not wealthy — resolved.' },
      { title: 'Story Tension', text: 'They sense the turning early. Some urge endurance; others say waiting is a luxury. The split: strike when Rome weakens, or shield the helpless?' },
      { title: 'Ritual & Obligation', text: 'A people of listening: offerings to river and stone, names of the dead spoken aloud. Beacons on high ground are summons, not mere signals.' },
      { title: 'What They Want', text: 'Protection — of people, memory, and place. Every blade raised is weighed against the cost to those who cannot run.' },
    ],
    keyPeople: [
      'Aerwyna — leadership as weight',
      'The Elders — memory-keepers',
      'Scouts — eyes of the mountains',
      'Families in refuge — the heart of decisions',
    ],
  },
  {
    id: 'deceangli',
    name: 'DECEANGLI',
    region: 'North Wales',
    image: '/images/tribes/deceangli.webp',
    subtitle:
      'Coast and seam — shaped by metal, labour, and the slow anger of the wounded earth.',
    blocks: [
      { title: 'The Land', text: 'Stone and seam. Coastal routes, worked hills, paths leading down into the dark. Mines open like mouths; the land gives — and remembers taking.' },
      { title: 'The People (after 196 CE)', text: 'Bound by labour as much as blood. People vanish into the hills and return changed — or not at all. Trade flows outward; cost stays behind.' },
      { title: 'Story Tension', text: 'Need versus resistance. Rome demands metal; families demand their people back. Rumours of captivity and forced labour spread like poison.' },
      { title: 'Ritual & Obligation', text: 'Offerings before the deep places — for mercy, not profit. Certain seams left untouched. Elders say the earth closes to those who forget respect.' },
      { title: 'What They Want', text: 'An end to the taking — even if it requires fire and blood to stop the ground swallowing their people.' },
    ],
    keyPeople: [
      'Mine-wardens — caught between demand and survival',
      'Coastal boatmen — traders and messengers',
      'Families of the taken — the true cost',
      'Quiet organisers — preparing in the dark',
    ],
  },
  {
    id: 'brigantes',
    name: 'BRIGANTES',
    region: 'North of the storylands',
    image: '/images/tribes/brigantes.webp',
    subtitle:
      'A northern weight — raiding pressure, opportunists, and would-be kings testing every border.',
    blocks: [
      { title: 'The Land', text: 'Wide country: moors, rivers, forests. Warbands move fast and vanish. From the south, their presence is measured in absence — cattle gone, messengers lost.' },
      { title: 'The People (after 196 CE)', text: 'Not one people but many, bound by ambition and fear. Authority fractures; war-leaders rise. Weakness is not tragedy — it is invitation.' },
      { title: 'Story Tension', text: 'They smell change: thinned garrisons and unwatched roads. Raids test borders and harvest captives as currency. How much can be taken before anyone stops them?' },
      { title: 'Ritual & Obligation', text: 'Oaths cut into skin. Spoils shared publicly. Cowardice remembered longer than defeat. Fear is a resource — cultivated carefully.' },
      { title: 'What They Want', text: 'Dominance: land, cattle, people. They do not plan for the world after Rome — they intend to own it.' },
    ],
    keyPeople: [
      'Warband leaders — violent, charismatic',
      'Scouts — testing every road',
      'Captive-takers — feeding markets',
      'Would-be kings — rising with the season',
    ],
  },
];

const CharactersByTribe = () => (
  <StoryworldCarousel
    items={TRIBES}
    title="Tribes & Peoples"
    subtitle="Northwest England, the Midlands, and North & Central Wales — post 196 CE"
    blurb="Explore land, loyalties, tensions, and ritual obligations shaping the storyworld."
    labelForItem={(tribe) => tribe.region}
  />
);

/* --------------------- Characters by Role --------------------- */

const ROLES = [
  {
    id: 'running-boar',
    name: 'THE RUNNING BOAR COHORT',
    tag: 'Role — Exiles & Brothers-in-arms',
    image: '/images/roles/running_boar.jpg',
    subtitle:
      'Disgraced soldiers and survivors bound by oath, endurance, and the hard discipline Rome taught them.',
    blocks: [
      {
        title: 'What defines them',
        text:
          'They carry Roman habits into wild ground: patrol routes, watch rotations, and the reflex to build defences with whatever the land offers.',
      },
      {
        title: 'Story tension',
        text:
          'They must protect families and allies without becoming the very occupation they fled. Every decision risks repeating Rome’s cruelties in a smaller mirror.',
      },
      {
        title: 'What they risk',
        text:
          'If they fail, the eagle’s memory dies with them — and the tribes will be left to face Rome’s reprisals alone.',
      },
    ],
    keyPeople: ['Varro', 'Julius', 'Lucius Cunovalos'],
  },
  {
    id: 'leaders-elders',
    name: 'LEADERS & ELDERS',
    tag: 'Role — Custodians of People',
    image: '/images/roles/leaders_elders.jpg',
    subtitle:
      'Those who hold the community together: negotiating, judging, remembering, and making choices that stain the soul but save lives.',
    blocks: [
      {
        title: 'What defines them',
        text:
          'Leadership is obligation, not glory. They trade certainty for survival — weighing hospitality, vengeance, and the sacred debt of oaths.',
      },
      {
        title: 'Story tension',
        text:
          'If they resist too early, Rome crushes them; if they bend too long, they lose the right to call themselves free.',
      },
      {
        title: 'What they risk',
        text:
          'Factions splitting inside the tribe — younger warbands against older counsellors — until unity becomes the rarest resource.',
      },
    ],
    keyPeople: ['Aerwyna', 'Cornovii counsellors', 'Elders of the hills'],
  },
  {
    id: 'warriors-champions',
    name: 'WARRIORS & CHAMPIONS',
    tag: 'Role — The Blade’s Edge',
    image: '/images/roles/warriors_champions.jpg',
    subtitle:
      'Guardians, raiders, and oath-bound fighters: the visible edge of power where reputation is forged and blood is answered.',
    blocks: [
      {
        title: 'What defines them',
        text:
          'They move fast, strike hard, and live under the eye of their people — praised when they protect, feared when they hunger for violence.',
      },
      {
        title: 'Story tension',
        text:
          'Champions are used as symbols — by chieftains, patrons, and rival tribes. Glory becomes a chain if it can be bought.',
      },
      {
        title: 'What they risk',
        text:
          'Becoming instruments rather than protectors — until the land itself feels polluted by their deeds.',
      },
    ],
    keyPeople: ['Tribal warbands', 'Cohort fighters', 'Raid leaders'],
  },
  {
    id: 'powerbrokers',
    name: 'POWERBROKERS & ENFORCERS',
    tag: 'Role — Control & Corruption',
    image: '/images/roles/powerbrokers_enforcers.jpg',
    subtitle:
      'Administrators, patrons, informers, and enforcers who turn lives into currency and call it order.',
    blocks: [
      {
        title: 'What defines them',
        text:
          'They thrive in paperwork and fear: debts, favours, denunciations, and the quiet threat of the arena or the chain-gang.',
      },
      {
        title: 'Story tension',
        text:
          'When imperial authority weakens, predators multiply. They scramble to keep their profit-streams alive by any means.',
      },
      {
        title: 'What they risk',
        text:
          'A world where the people remember their names — and memory becomes judgement.',
      },
    ],
    keyPeople: ['Paulus Catena', 'Patrons', 'Informers & fixers'],
  },
];

const CharactersByRole = () => (
  <StoryworldCarousel
    items={ROLES}
    title="Characters by Role"
    subtitle="How people function in the storyworld — post 196 CE"
    blurb="Explore exiles, custodians, champions, and the machinery of control without squeezing the archive into small card backs."
    labelForItem={(role) => role.tag}
  />
);

/* --------------------- Characters by Allegiance --------------------- */

const ALLEGIANCES = [
  {
    id: 'rome-severan',
    name: 'ROME — SEVERAN ORDER',
    tag: 'Allegiance — Empire',
    image: '/images/allegiances/rome_severan.jpg',
    subtitle:
      'Fortresses, law, punishment, and profit — stability on the surface, fear beneath.',
    blocks: [
      {
        title: 'How it presents itself',
        text:
          'Order, roads, law, and “peace.” Rome claims to civilise — while extracting wealth and enforcing compliance through spectacle and force.',
      },
      {
        title: 'How it really works here',
        text:
          'In the borderlands, authority depends on informers, debts, and controlled violence. The arena and the prison do what speeches cannot.',
      },
      {
        title: 'Pressure point',
        text:
          'After 196 CE, Rome is stretched. Weakness invites corruption — and corruption invites revolt.',
      },
    ],
    keyPeople: ['Commanders', 'Officials', 'Paulus Catena’s network'],
  },
  {
    id: 'running-boar',
    name: 'THE RUNNING BOAR — LOYALTY TO BROTHERS',
    tag: 'Allegiance — Oath',
    image: '/images/allegiances/running_boar_oath.jpg',
    subtitle:
      'A new banner for men without a state: loyalty to each other, and to a future they must carve by hand.',
    blocks: [
      {
        title: 'What binds them',
        text:
          'Shared loss and shared skill. They move like soldiers, but their purpose is no longer Rome’s command — it is survival with dignity.',
      },
      {
        title: 'Conflict inside the allegiance',
        text:
          'Some still dream of Rome’s ideals; others want only revenge. The bond holds — until it is tested by impossible choices.',
      },
      {
        title: 'Pressure point',
        text:
          'The eagle’s fate becomes a spiritual weight: a symbol that can heal them, or break them.',
      },
    ],
    keyPeople: ['Varro', 'Julius', 'Lucius Cunovalos'],
  },
  {
    id: 'tribal-resistance',
    name: 'TRIBAL RESISTANCE & REFUGE',
    tag: 'Allegiance — Land & People',
    image: '/images/allegiances/tribal_resistance.jpg',
    subtitle:
      'Protection before glory: sheltering families, guarding sacred places, and striking only when the land allows.',
    blocks: [
      {
        title: 'What drives them',
        text:
          'Survival with identity intact. They keep rituals, remember names, and refuse to let Rome decide what “civilised” means.',
      },
      {
        title: 'Where it fractures',
        text:
          'Warriors push for action; elders weigh the cost. A single raid can save the people — or summon annihilation.',
      },
      {
        title: 'Pressure point',
        text:
          'Captives and forced labour: once the taking begins, restraint becomes harder to preach.',
      },
    ],
    keyPeople: ['Aerwyna', 'Elders', 'Scouts and protectors'],
  },
  {
    id: 'brigantes-pressure',
    name: 'BRIGANTES — RAID & DOMINION',
    tag: 'Allegiance — Ambition',
    image: '/images/allegiances/brigantes_ambition.jpg',
    subtitle:
      'A northern force testing every border: cattle, captives, prestige — and the hunger to rule what Rome cannot hold.',
    blocks: [
      {
        title: 'How they operate',
        text:
          'Fast warbands, shifting loyalties, and opportunism. They take what they can, then vanish — leaving fear like smoke on the wind.',
      },
      {
        title: 'Why they matter to the story',
        text:
          'They are the external pressure that forces alliances. Even enemies must bargain when the raiders come.',
      },
      {
        title: 'Pressure point',
        text:
          'When Rome weakens, they surge — and every tribe must decide whether to unite or be picked apart.',
      },
    ],
    keyPeople: ['Warband leaders', 'Scouts', 'Captive-takers'],
  },
];

const CharactersByAllegiance = () => (
  <StoryworldCarousel
    items={ALLEGIANCES}
    title="Characters by Allegiance"
    subtitle="Who people serve, follow, or fear — post 196 CE"
    blurb="Explore the main loyalties shaping the story: empire, oath-bound exiles, land-bound resistance, and northern ambition."
    labelForItem={(faction) => faction.tag}
  />
);

const CharactersTimeline = () => <SceneTimeline />;

/* --------------------- Scene Timeline (Story-led) --------------------- */

const TIMELINE_BOOKS = {
  blood: {
    title: 'Blood and Honour',
    subtitle: 'Legio XX: Blood and Honour',
    blurb:
      'Post-196 CE — a story bound to land, loyalty, and consequence. Move through each phase as a cinematic archive of turning points.',
    phases: [
      {
        phase: 'PHASE I — The March of the Twentieth',
        subtitle: 'Order before the fracture',
        image: '/images/characters/timeline/phase1.jpg',
        whatChanges: 'Rome’s unity collapses.',
        whatCosts: 'Faith in imperial honour.',
        scenes: [
          {
            title: 'Call to Arms',
            location: 'Deva Victrix',
            pov: 'Varro',
            image: '/images/characters/timeline/call_to_arms.jpg',
            summary:
              'Varro leads the first cohort of the Twentieth from Deva. Ritual binds the legion to duty before survival becomes the true command.',
          },
          {
            title: 'Ashes Between Eagles',
            location: 'Roads of Gaul',
            pov: 'Varro',
            image: '/images/characters/timeline/ashes_between_eagles.jpg',
            summary:
              'The march south exposes the empire’s cracks. Civilians flee, loyalties thin, and honour begins to feel conditional.',
          },
          {
            title: 'Lugdunum',
            location: 'The Field of Decision',
            pov: 'Varro',
            image: '/images/characters/timeline/lugdunum.jpg',
            summary:
              'Rome turns upon itself. Discipline holds, but belief does not. Victory carries no absolution.',
          },
        ],
      },
      {
        phase: 'PHASE II — Arena and Oath',
        subtitle: 'Unmaking',
        image: '/images/characters/timeline/phase2.jpg',
        whatChanges: 'Loyalty turns inward.',
        whatCosts: 'The illusion of return.',
        scenes: [
          {
            title: 'Broken, Not Defeated',
            location: 'After Lugdunum',
            pov: 'Varro',
            image: '/images/characters/timeline/broken_not_defeated.jpg',
            summary:
              'Survivors are sorted by usefulness. Rank and name are stripped away. Varro becomes property.',
          },
          {
            title: 'The Trial of Chains',
            location: 'Deva',
            pov: 'Varro',
            image: '/images/characters/timeline/the_trial_of_chains.jpg',
            summary:
              'Former soldiers are trained to fight as spectacle. Formation becomes liability. Survival becomes instinct.',
          },
          {
            title: 'Blood and Beasts',
            location: 'The Arena',
            pov: 'Varro',
            image: '/images/characters/timeline/thumb_blood_and_beasts.jpg',
            summary:
              'Varro faces Brigantes while Cornovii elites watch. Justice is replaced by entertainment.',
          },
        ],
      },
      {
        phase: 'PHASE III — The Rising Flame',
        subtitle: 'Awakening',
        image: '/images/characters/timeline/phase3.jpg',
        whatChanges: 'Resistance gains direction.',
        whatCosts: 'Safety through obscurity.',
        scenes: [
          {
            title: 'Chains of the Ambitious',
            location: 'Catena’s World',
            pov: 'Varro',
            image: '/images/characters/timeline/chains_of_the_ambitious.jpg',
            summary:
              'Power is revealed as transaction. Lives are currency. Silence is profit.',
          },
          {
            title: 'Grave Tidings',
            location: 'Knowledge Arrives',
            pov: 'Varro',
            image: '/images/characters/timeline/grave_tidings.jpg',
            summary:
              'Varro understands the machinery behind the cruelty. The enemy is systemic, not singular.',
          },
          {
            title: 'Ridgeway Awakens',
            location: 'High Ground',
            pov: 'Varro / Aerwyna',
            image: '/images/characters/timeline/ridgeway_awakens.jpg',
            summary:
              'Beacons, watchers, and old paths reassert their purpose. The land begins to speak.',
          },
          {
            title: 'Plans Forged in Fire',
            location: 'Oath Renewed',
            pov: 'Varro',
            image: '/images/characters/timeline/plans_forged_in_fire.jpg',
            summary:
              'Escape becomes intent. Brotherhood replaces hierarchy.',
          },
        ],
      },
      {
        phase: 'PHASE IV — Fire and Rebellion',
        subtitle: 'Consequences spread',
        image: '/images/characters/timeline/phase4.jpg',
        whatChanges: 'The conflict widens beyond one man.',
        whatCosts: 'Any hope of restraint.',
        scenes: [
          {
            title: 'Painted Shadows',
            location: 'Ritual and Interrogation',
            pov: 'Aerwyna',
            image: '/images/characters/timeline/painted_shadows.jpg',
            summary:
              'Truth is drawn out through fear and rite. The tribes step fully into the story.',
          },
          {
            title: 'The Fractured Alliance',
            location: 'Debts and Promises',
            pov: 'Multiple',
            image: '/images/characters/timeline/the_fractured_alliance.jpg',
            summary:
              'Cornovii obligation, Roman pressure, and tribal memory collide.',
          },
          {
            title: 'Into Deva',
            location: 'Return',
            pov: 'Varro',
            image: '/images/characters/timeline/into_deva.jpg',
            summary:
              'Varro enters the fortress to reclaim the eagle, spirit of the Twentieth.',
          },
        ],
      },
      {
        phase: 'PHASE V — The Reckoning',
        subtitle: 'Irreversible movement',
        image: '/images/characters/timeline/phase5.jpg',
        whatChanges: 'Leadership shifts from reaction to choice.',
        whatCosts: 'All remaining claims to imperial legitimacy.',
        scenes: [
          {
            title: 'Beneath the Standard',
            location: 'Meaning Reclaimed',
            pov: 'Varro',
            image: '/images/characters/timeline/beneath_the_standard.jpg',
            summary:
              'The Eagle is no longer Rome’s alone. It becomes memory and vow.',
          },
          {
            title: 'No Way but the Water',
            location: 'Flight',
            pov: 'Varro',
            image: '/images/characters/timeline/no_way_but_the_water.jpg',
            summary:
              'Escape is chosen over martyrdom. Survival becomes strategy.',
          },
          {
            title: 'The Fort and the Flame',
            location: 'Denial of Power',
            pov: 'Varro / Cohort',
            image: '/images/characters/timeline/the_fort_and_the_flame.jpg',
            summary:
              'Fire is used with purpose. Rome is weakened, not avenged.',
          },
        ],
      },
      {
        phase: 'PHASE VI — The Last Command',
        subtitle: 'Legacy over survival',
        image: '/images/characters/timeline/phase6.jpg',
        whatChanges: 'The struggle becomes custodial.',
        whatCosts: 'The myth of Rome’s eternity.',
        scenes: [
          {
            title: 'The Return of the Boar',
            location: 'Identity Reforged',
            pov: 'Varro',
            image: '/images/characters/timeline/the_return_of_the_boar.jpg',
            summary:
              'The cohort is reborn: neither Roman nor tribal, but bound by oath and place.',
          },
          {
            title: 'Where Earth Remembers Iron',
            location: 'Witness',
            pov: 'Chorus',
            image: '/images/characters/timeline/where_earth_remembers_iron.jpg',
            summary:
              'Weapons are laid down. The land absorbs what men cannot carry.',
          },
          {
            title: 'The Eagle’s End',
            location: 'Transformation',
            pov: 'Varro',
            image: '/images/characters/timeline/the_eagles_end.jpg',
            summary:
              'The symbol is neither destroyed nor reclaimed. It is released.',
          },
        ],
      },
      {
        phase: 'PHASE VII — Watchers and Unity',
        subtitle: 'Aftermath that matters',
        image: '/images/characters/timeline/phase7.jpg',
        whatChanges: 'Resistance becomes inheritance.',
        whatCosts: 'Simple answers.',
        scenes: [
          {
            title: 'Refuge of the Fallen',
            location: 'Defence',
            pov: 'Multiple',
            image: '/images/characters/timeline/refuge_of_the_fallen.jpg',
            summary:
              'Families, survivors, and scouts gather. Protection replaces conquest.',
          },
          {
            title: 'The Seer and the Flame',
            location: 'Vision',
            pov: 'Aerwyna / Seer',
            image: '/images/characters/timeline/the_seer_and_the_flame.jpg',
            summary:
              'The struggle is reframed beyond vengeance.',
          },
          {
            title: 'The Path to Unity',
            location: 'Alignment',
            pov: 'Aerwyna',
            image: '/images/characters/timeline/the_path_to_unity.jpg',
            summary:
              'Tribes choose cooperation over isolation.',
          },
          {
            title: 'The Wind Knows Their Names',
            location: 'Memory',
            pov: 'Chorus',
            image: '/images/characters/timeline/the_wind_knows_their_names.jpg',
            summary:
              'The land remembers those who stood, and those who fell.',
          },
        ],
      },
    ],
  },

  shadows: {
    title: 'Shadows of the Eagle',
    subtitle: 'Sons of the Standard: Book Two',
    blurb:
      'After the eagle falls, Britannia must decide what can still be saved. Move through each phase as an archive of oath, sacrifice, and memory.',
    phases: [
      {
        phase: 'PHASE I — Ashes and Oaths',
        subtitle: 'The Eagle becomes memory',
        image: '/images/books/shadows-chronicle/ashes-and-oaths.webp',
        whatChanges: 'The old standard is released.',
        whatCosts: 'Varro must surrender the symbol that once defined him.',
        scenes: [
          {
            title: 'Ashes and Oaths',
            location: 'The Lake of the Eagle',
            pov: 'Varro',
            image: '/images/books/shadows-chronicle/ashes-and-oaths.webp',
            summary:
              'Varro casts the eagle into the water, refusing to let Rome’s old symbol divide the living.',
          },
          {
            title: 'The Fracture with Memory',
            location: 'Sacred Ground',
            pov: 'Varro / Lucius',
            image: '/images/books/shadows-chronicle/fracture-with-memory.webp',
            summary:
              'Memory becomes burden as Varro and Lucius confront what the eagle meant, and what it can no longer mean.',
          },
          {
            title: 'The Ground Chosen',
            location: 'The Ridge',
            pov: 'Lucius',
            image: '/images/books/shadows-chronicle/ground-chosen.webp',
            summary:
              'Varro and Lucius look across the land and understand that survival depends upon choosing where to stand.',
          },
        ],
      },
      {
        phase: 'PHASE II — The Gathering Storm',
        subtitle: 'Unity is argued before it is sworn',
        image: '/images/books/shadows-chronicle/judgement-of-the-living.webp',
        whatChanges: 'Tribes begin to listen.',
        whatCosts: 'Trust is fragile, and pride is dangerous.',
        scenes: [
          {
            title: 'The Judgement of the Living',
            location: 'Ordovices Territory',
            pov: 'Aerwyna',
            image: '/images/books/shadows-chronicle/judgement-of-the-living.webp',
            summary:
              'The living judge whether Varro and his followers are liberators, invaders, or another burden upon a wounded land.',
          },
          {
            title: 'The Forge',
            location: 'Mona',
            pov: 'Aerwyna',
            image: '/images/books/shadows-chronicle/the-forge.webp',
            summary:
              'Weapons are shaped in heat and labour, and Aerwyna’s authority is tempered through action.',
          },
          {
            title: 'The Parley',
            location: 'Frontier Road',
            pov: 'Varro',
            image: '/images/books/shadows-chronicle/the-parley.webp',
            summary:
              'Roman horsemen and tribal warriors meet beneath a fragile truce, each side expecting betrayal.',
          },
          {
            title: 'The Price of Unity',
            location: 'Tribal Hall',
            pov: 'Lucius',
            image: '/images/books/shadows-chronicle/price-of-unity.webp',
            summary:
              'Lucius and Varro confront the cost of alliance, leadership, and divided loyalties.',
          },
        ],
      },
      {
        phase: 'PHASE III — Fire and Salt',
        subtitle: 'The war reaches the shore',
        image: '/images/books/shadows-chronicle/edge-of-salt.webp',
        whatChanges: 'The conflict widens beyond the land.',
        whatCosts: 'The enemy trades in lives, not merely territory.',
        scenes: [
          {
            title: 'The Taking',
            location: 'Western Shore',
            pov: 'Nestor',
            image: '/images/books/shadows-chronicle/the-taking.webp',
            summary:
              'Raiders carry away the living, and helplessness becomes one of the book’s deepest wounds.',
          },
          {
            title: 'Ore and Oath',
            location: 'Mona Mines',
            pov: 'Captives',
            image: '/images/books/shadows-chronicle/ore-and-oath.webp',
            summary:
              'Mona’s wealth is revealed through the suffering of those forced beneath the earth.',
          },
          {
            title: 'The Edge of Salt',
            location: 'Gangani Coast',
            pov: 'Scouts',
            image: '/images/books/shadows-chronicle/edge-of-salt.webp',
            summary:
              'Sails gather on the western water as the struggle spreads to coast, kinship, and sea-road.',
          },
          {
            title: 'Wrath of the Forgotten',
            location: 'Mona',
            pov: 'Aerwyna',
            image: '/images/books/shadows-chronicle/wrath-of-the-forgotten.webp',
            summary:
              'Aerwyna leads the raid to free those held in bondage, turning memory into action.',
          },
        ],
      },
      {
        phase: 'PHASE IV — The Last Lights',
        subtitle: 'The land witnesses what the living choose',
        image: '/images/books/shadows-chronicle/oath-that-remains.webp',
        whatChanges: 'The tribes swear to a future no empire can grant.',
        whatCosts: 'Every oath carries the dead within it.',
        scenes: [
          {
            title: 'The Double Enemy',
            location: 'Coastal Stronghold',
            pov: 'Julius / Varro',
            image: '/images/books/shadows-chronicle/double-enemy.webp',
            summary:
              'Julius makes his final stand while the alliance fights threats from within and without.',
          },
          {
            title: 'The Oath that Remains',
            location: 'Ridge Summit',
            pov: 'Aerwyna',
            image: '/images/books/shadows-chronicle/oath-that-remains.webp',
            summary:
              'The tribes choose unity despite every reason to fracture.',
          },
          {
            title: 'The Lights that Remain',
            location: 'The Ridge',
            pov: 'The Druid',
            image: '/images/books/shadows-chronicle/lights-that-remain.webp',
            summary:
              'Blue-white orbs appear as silent witnesses, as old as the land itself.',
          },
          {
            title: 'The Weight of Wings',
            location: 'Britannia',
            pov: 'Varro / Lucius / Aerwyna',
            image: '/images/books/shadows-chronicle/weight-of-wings.webp',
            summary:
              'The future remains uncertain, but hope survives through those willing to carry its weight.',
          },
        ],
      },
    ],
  },

  sons: {
    title: 'Sons of the Standard',
    subtitle: 'Sons of the Standard: Book Three',
    blurb:
      'Britannia is being hollowed by ledgers, chains, and fire. Move through twelve moments of rescue, memory, alliance, and the birth of a people beyond Rome.',
    phases: [
      {
        phase: 'PHASE I — The Hollowing',
        subtitle: 'People are counted, sorted, and taken',
        image: '/images/books/sons-chronicle/01-ash-that-does-not-settle.webp',
        whatChanges: 'The enemy is revealed as a system of extraction rather than an army.',
        whatCosts: 'The land loses its young, strong, and skilled while survivors carry the fear.',
        scenes: [
          {
            title: 'The Ash That Does Not Settle',
            location: 'The Chosen Ridge',
            pov: 'Varro / Lucius',
            image: '/images/books/sons-chronicle/01-ash-that-does-not-settle.webp',
            summary:
              'Varro and Lucius watch ash drift from burning settlements as a scout reports that captives are being sorted and marked like livestock.',
          },
          {
            title: 'The Physician in the Chain',
            location: 'The Extraction Route',
            pov: 'Nestor',
            image: '/images/books/sons-chronicle/02-physician-in-the-chain.webp',
            summary:
              'Kept alive for his medical skill, Nestor treats the captives while memorising the routes, overseers, and names within the chain.',
          },
          {
            title: 'The Women of the Ridge',
            location: 'Valley Holding Pens',
            pov: 'Lucius / The Women',
            image: '/images/books/sons-chronicle/03-women-of-the-ridge.webp',
            summary:
              'When panic threatens the first rescue, the women of the ridge calm the captives and turn a frightened crowd into an organised retreat.',
          },
        ],
      },
      {
        phase: 'PHASE II — Names Against Numbers',
        subtitle: 'Memory becomes resistance',
        image: '/images/books/sons-chronicle/04-rearguard-refused.webp',
        whatChanges: 'The coalition rejects solitary sacrifice and begins to understand the scale of Rome’s design.',
        whatCosts: 'Leaders must live with grief, bear witness, and resist the temptation of a clean death.',
        scenes: [
          {
            title: 'The Rearguard Refused',
            location: 'The Valley Road',
            pov: 'Varro / Lucius',
            image: '/images/books/sons-chronicle/04-rearguard-refused.webp',
            summary:
              'Lucius prevents Varro from repeating Julius’s sacrifice, insisting that the lesson is not for one man to die, but for everyone to stand together.',
          },
          {
            title: 'The Ledger of Men',
            location: 'Northern Administration',
            pov: 'Nestor',
            image: '/images/books/sons-chronicle/05-ledger-of-men.webp',
            summary:
              'Nestor discovers a Roman registry that reduces entire tribal populations to columns of reallocation, usefulness, and expenditure.',
          },
          {
            title: 'The Druid’s Gathering',
            location: 'Hidden Valleys',
            pov: 'The Druid / Survivors',
            image: '/images/books/sons-chronicle/06-druids-gathering.webp',
            summary:
              'The druid asks survivors to speak the names of the taken aloud, turning memory into a living answer to Rome’s ledgers.',
          },
        ],
      },
      {
        phase: 'PHASE III — The Western Answer',
        subtitle: 'Purpose, kinship, and betrayal reshape the war',
        image: '/images/books/sons-chronicle/07-fighting-for-what-cannot-be-counted.webp',
        whatChanges: 'The struggle shifts from retaliation to retrieval, and the western sea becomes a road of alliance.',
        whatCosts: 'The coalition must trust distant kin while its enemies turn upon one another.',
        scenes: [
          {
            title: 'Fighting for What Cannot Be Counted',
            location: 'Council on the Ridge',
            pov: 'Aerwyna / Varro',
            image: '/images/books/sons-chronicle/07-fighting-for-what-cannot-be-counted.webp',
            summary:
              'Aerwyna rejects a war measured only in swords and supplies, declaring that they must fight for memory, kinship, and the recovery of their people.',
          },
          {
            title: 'The Sea Answers',
            location: 'The Western Ridge',
            pov: 'Gangani Messenger / Coalition',
            image: '/images/books/sons-chronicle/08-the-sea-answers.webp',
            summary:
              'A Gangani rider brings word that the Hibernian tribes have heard the warning and are gathering a fleet to cross the western sea.',
          },
          {
            title: 'The Silver Betrayal',
            location: 'Didrugaros’s Villa',
            pov: 'Didrugaros',
            image: '/images/books/sons-chronicle/09-the-silver-betrayal.webp',
            summary:
              'Didrugaros secretly gives Brennos’s camp locations to Roman overseers, sacrificing his ally to protect the profits of the extraction routes.',
          },
        ],
      },
      {
        phase: 'PHASE IV — A People Beyond Rome',
        subtitle: 'The chain is broken and the future crosses the sea',
        image: '/images/books/sons-chronicle/10-the-breaking-of-the-chain.webp',
        whatChanges: 'Retrieval succeeds, the Running Boar becomes the banner of a people, and the sea opens toward a new life.',
        whatCosts: 'Victory requires leaving scarred Britannia and carrying its memory into an uncertain future.',
        scenes: [
          {
            title: 'The Breaking of the Chain',
            location: 'Mona and the Coastal Pens',
            pov: 'Varro / Aerwyna / Lucius',
            image: '/images/books/sons-chronicle/10-the-breaking-of-the-chain.webp',
            summary:
              'The coalition and Hibernian fleet strike the camps, convoys, and holding pens together, breaking the chains, reuniting families, and rescuing Nestor.',
          },
          {
            title: 'The Standard Without Eagle',
            location: 'Ridge Above the Sea',
            pov: 'Varro / The People',
            image: '/images/books/sons-chronicle/11-the-standard-without-eagle.webp',
            summary:
              'Beneath the battered Running Boar, Varro declares that the survivors are no longer an army but a people; the absent Eagle is not mourned.',
          },
          {
            title: 'The Light Beyond the Ridge',
            location: 'The Hibernian Shore',
            pov: 'Varro',
            image: '/images/books/sons-chronicle/12-the-light-beyond-the-ridge.webp',
            summary:
              'The survivors cross in Hibernian curraghs, and Varro watches their campfires from the shore as the burden of command finally begins to lift.',
          },
        ],
      },
    ],
  },

};

const getTimelineThumb = (imgPath) => {
  if (!imgPath || typeof imgPath !== 'string') return null;
  if (imgPath.includes('/thumb_')) return imgPath;
  const parts = imgPath.split('/');
  const file = parts.pop();
  return [...parts, `thumb_${file}`].join('/');
};

const buildImageCandidates = (path) => {
  if (!path || typeof path !== 'string') return [];
  const p = path.trim();
  const out = [p];

  const swapExt = (ext) => {
    const lower = p.toLowerCase();
    if (lower.endsWith(ext)) return p.slice(0, -ext.length);
    return null;
  };

  const base =
    swapExt('.jpg') ||
    swapExt('.jpeg') ||
    swapExt('.png') ||
    swapExt('.webp');

  if (base) {
    out.push(base + '.jpg');
    out.push(base + '.jpeg');
    out.push(base + '.png');
    out.push(base + '.webp');
  }

  if (!p.includes('/thumb_')) {
    const parts = p.split('/');
    const file = parts.pop();
    out.push([...parts, `thumb_${file}`].join('/'));
  }

  return [...new Set(out.filter(Boolean))];
};

const TimelineImg = ({ candidates, src, alt, className, ...rest }) => {
  const list = React.useMemo(() => {
    const fromCandidates = Array.isArray(candidates)
      ? candidates.flatMap(buildImageCandidates)
      : [];
    const fromSrc = src ? buildImageCandidates(src) : [];
    return [...new Set([...fromCandidates, ...fromSrc].filter(Boolean))];
  }, [candidates, src]);

  const [idx, setIdx] = useState(0);
  const current = list[idx];

  if (!current) return null;

  return (
    <img
      src={current}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => {
        setIdx((i) => (i + 1 < list.length ? i + 1 : list.length));
      }}
      {...rest}
    />
  );
};

const SceneTimeline = () => {
  const [activeBookId, setActiveBookId] = useState('blood');
  const [activeIndex, setActiveIndex] = useState(0);

  const activeBook = TIMELINE_BOOKS[activeBookId] || TIMELINE_BOOKS.blood;
  const timeline = activeBook.phases;
  const active = timeline[activeIndex] || timeline[0];

  const switchBook = (bookId) => {
    setActiveBookId(bookId);
    setActiveIndex(0);
  };

  const previous = () =>
    setActiveIndex((i) => (i - 1 + timeline.length) % timeline.length);

  const next = () =>
    setActiveIndex((i) => (i + 1) % timeline.length);

  if (!active) return null;

  return (
    <div className="min-h-screen bg-stone-950 py-0 px-6 pb-16 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(circle_at_18%_12%,_rgba(251,191,36,0.18)_0,_transparent_30%),radial-gradient(circle_at_78%_18%,_rgba(249,115,22,0.14)_0,_transparent_34%),linear-gradient(180deg,_rgba(255,255,255,0.04)_0,_transparent_22%,_rgba(0,0,0,0.35)_100%)]" />

      <div className="relative z-10">
        <HeaderSection
          size="sm"
          title="Scene Timeline"
          subtitle={activeBook.subtitle}
          blurb={activeBook.blurb}
        />

        <div className="max-w-6xl mx-auto mt-6 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => switchBook('blood')}
            className={`px-5 py-2 rounded-full border text-sm font-bold transition-colors ${
              activeBookId === 'blood'
                ? 'bg-amber-300 text-stone-950 border-amber-300'
                : 'bg-black/30 text-amber-100 border-amber-200/25 hover:bg-amber-200/10'
            }`}
          >
            Blood and Honour
          </button>

          <button
            type="button"
            onClick={() => switchBook('shadows')}
            className={`px-5 py-2 rounded-full border text-sm font-bold transition-colors ${
              activeBookId === 'shadows'
                ? 'bg-amber-300 text-stone-950 border-amber-300'
                : 'bg-black/30 text-amber-100 border-amber-200/25 hover:bg-amber-200/10'
            }`}
          >
            Shadows of the Eagle
          </button>

          <button
            type="button"
            onClick={() => switchBook('sons')}
            className={`px-5 py-2 rounded-full border text-sm font-bold transition-colors ${
              activeBookId === 'sons'
                ? 'bg-amber-300 text-stone-950 border-amber-300'
                : 'bg-black/30 text-amber-100 border-amber-200/25 hover:bg-amber-200/10'
            }`}
          >
            Sons of the Standard
          </button>
        </div>

        <div className="max-w-6xl mx-auto mt-10">
          <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-7 items-stretch">
            <div className="relative rounded-2xl overflow-hidden border border-amber-200/20 bg-black shadow-2xl shadow-black/40 min-h-[300px] md:min-h-[500px]">
              <TimelineImg
                key={`${activeBookId}-${active.phase}-banner`}
                src={active.image}
                candidates={[
                  active.image,
                  active.scenes?.[0]?.image,
                  getTimelineThumb(active.scenes?.[0]?.image),
                ]}
                alt={active.phase}
                className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-black/10" />

              <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-[11px] tracking-wide uppercase bg-amber-300/10 border border-amber-200/25 text-amber-100">
                  Phase {activeIndex + 1} of {timeline.length}
                </div>

                <h2 className="mt-3 text-2xl md:text-4xl font-bold text-white tracking-wide leading-tight">
                  {active.phase}
                </h2>

                <p className="mt-3 text-sm md:text-base text-stone-200 leading-relaxed max-w-xl">
                  {active.subtitle}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200/20 bg-gradient-to-br from-stone-950 via-stone-900 to-black shadow-2xl shadow-black/40 overflow-hidden">
              <div className="relative p-6 md:p-7">
                <div className="absolute inset-0 pointer-events-none opacity-35 bg-[radial-gradient(circle_at_18%_8%,_rgba(251,191,36,0.22)_0,_transparent_28%),radial-gradient(circle_at_82%_20%,_rgba(249,115,22,0.14)_0,_transparent_32%)]" />

                <div className="relative">
                  <div className="flex items-center justify-between gap-4 mb-5">
                    <div>
                      <p className="text-xs uppercase tracking-[0.22em] text-amber-200/80">
                        Timeline Archive
                      </p>
                      <h3 className="mt-1 text-xl md:text-2xl font-bold text-amber-50">
                        {active.phase}
                      </h3>
                    </div>

                    <div className="text-sm text-stone-400 whitespace-nowrap">
                      {activeIndex + 1} / {timeline.length}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                    <section className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                      <h4 className="text-xs font-bold uppercase tracking-wide text-amber-200">
                        What Changes
                      </h4>
                      <p className="mt-2 text-sm text-stone-300 leading-relaxed">
                        {active.whatChanges}
                      </p>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                      <h4 className="text-xs font-bold uppercase tracking-wide text-amber-200">
                        What It Costs
                      </h4>
                      <p className="mt-2 text-sm text-stone-300 leading-relaxed">
                        {active.whatCosts}
                      </p>
                    </section>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    {active.scenes.map((scene) => (
                      <article
                        key={scene.title}
                        className="rounded-xl border border-white/10 bg-black/25 overflow-hidden"
                      >
                        <div className="grid grid-cols-[88px_1fr] md:grid-cols-[118px_1fr] gap-3">
                          <div className="relative min-h-[100px]">
                            <TimelineImg
                              key={`${activeBookId}-${scene.title}-thumb`}
                              src={scene.image}
                              candidates={[
                                scene.image,
                                getTimelineThumb(scene.image),
                                active.image,
                              ]}
                              alt={scene.title}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/15" />
                          </div>

                          <div className="py-3 pr-3">
                            <div className="flex flex-wrap gap-2 mb-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-amber-300/10 border border-amber-200/15 text-amber-100">
                                {scene.location}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-white/5 border border-white/10 text-stone-200">
                                POV: {scene.pov}
                              </span>
                            </div>

                            <h4 className="text-base md:text-lg font-bold text-white">
                              {scene.title}
                            </h4>

                            <p className="mt-1 text-sm text-stone-300 leading-relaxed">
                              {scene.summary}
                            </p>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={previous}
                        className="px-4 py-2 rounded-full border border-amber-200/25 bg-black/30 text-amber-100 text-sm hover:bg-amber-200/10 transition-colors"
                      >
                        ← Previous phase
                      </button>

                      <button
                        type="button"
                        onClick={next}
                        className="px-4 py-2 rounded-full border border-amber-200/25 bg-black/30 text-amber-100 text-sm hover:bg-amber-200/10 transition-colors"
                      >
                        Next phase →
                      </button>
                    </div>

                    <div className="flex gap-2 justify-center sm:justify-end">
                      {timeline.map((phase, idx) => (
                        <button
                          key={phase.phase}
                          type="button"
                          onClick={() => setActiveIndex(idx)}
                          aria-label={`Show ${phase.phase}`}
                          className={`h-2.5 rounded-full transition-all ${
                            idx === activeIndex
                              ? 'w-8 bg-amber-300'
                              : 'w-2.5 bg-white/25 hover:bg-white/45'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`mt-6 grid grid-cols-2 md:grid-cols-4 ${
              timeline.length >= 7 ? 'lg:grid-cols-7' : 'lg:grid-cols-4'
            } gap-3`}
          >
            {timeline.map((phase, idx) => (
              <button
                key={phase.phase}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className={`group relative rounded-xl overflow-hidden border text-left min-h-[104px] transition-all ${
                  idx === activeIndex
                    ? 'border-amber-300/70 shadow-lg shadow-orange-950/30'
                    : 'border-white/10 hover:border-amber-200/40'
                }`}
              >
                <TimelineImg
                  key={`${activeBookId}-${phase.phase}-rail`}
                  src={phase.image}
                  candidates={[
                    phase.image,
                    phase.scenes?.[0]?.image,
                    getTimelineThumb(phase.scenes?.[0]?.image),
                  ]}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-transparent" />

                <div className="relative z-10 p-3 flex h-full items-end">
                  <span className="text-xs md:text-sm font-bold text-white leading-tight">
                    {phase.phase.replace(' — ', '\n')}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};



export {
  Characters,
  CharactersByTribe,
  CharactersByRole,
  CharactersByAllegiance,
  CharactersTimeline,
  Media,
  Timeline,
  SceneTimeline,
  Research,
  Maps,
};
